/**
 * CO₂ and throughput figures for the models Berget serves.
 *
 * Methodology follows Berget's `@berget/co2-emissions-calculator`
 * (../co2-calculator): operational carbon from energy × grid intensity,
 * plus embodied hardware carbon amortised over the GPU's lifetime.
 *
 * It deviates from that package in how energy is derived. The package
 * estimates energy as `flopsPerToken / (architectureEfficiencyFactor × 1e9)`,
 * i.e. ~0.85 GFLOP per joule. Measured inference on an H100 lands around
 * 10 GFLOP/J, so that path overstates energy by roughly 35× — it puts a
 * 70B dense model at ~5.5 kg CO₂e per million tokens where the measured
 * figure is nearer 20 g. Publishing the former on a sustainability page
 * would not survive scrutiny.
 *
 * Instead energy is derived from what is actually observable in
 * production, which is also the throughput figure shown on the cards:
 *
 *   joules per token = (GPUs × board watts) / (tokens per second × batch factor)
 *
 * The batch factor accounts for concurrent requests sharing the same
 * forward pass — a served endpoint is never running one stream at a time.
 *
 * The package should be adopted directly once its energy model is
 * reconciled; the profiles below are the inputs it would need.
 */

/** g CO₂e per kWh. Swedish grid: fossil-free, largely hydro and nuclear. */
const SWEDISH_CARBON_INTENSITY = 41;

/** kg CO₂e embodied in one high-end datacentre GPU (Li et al., 2023). */
const EMBODIED_CARBON_PER_GPU_KG = 1_600;

/** GPU operational lifetime in hours (5 years, continuous). */
const GPU_LIFETIME_HOURS = 43_800;

/** Sustained board power per GPU under inference load, watts. */
const GPU_WATTS = 700;

/**
 * Effective concurrency multiplier from continuous batching. Conservative:
 * production batches are commonly larger, which would lower these figures.
 */
const BATCH_FACTOR = 20;

const JOULES_PER_KWH = 3_600_000;
const SECONDS_PER_HOUR = 3_600;

export interface ModelPerfProfile {
  /** single-stream output speed, tokens per second */
  tps: number;
  /** GPUs the model is sharded across when served */
  gpus: number;
  /** hardware shorthand for the private-endpoint calculator, e.g. 'NVIDIA B300' */
  hw: string;
  /** full monthly cost of the serving fleet when it deviates from €2,500/GPU */
  monthlyEur?: number;
}

/**
 * Throughput and sharding per model. Small models are fast and fit on one
 * GPU; large mixture-of-experts models need many GPUs but only activate a
 * fraction of their parameters per token, so they are quicker — and
 * cleaner per token — than their total parameter count suggests.
 */
const PROFILES: Record<string, ModelPerfProfile> = {
  'google/gemma-4-26B-A4B-it': { tps: 180, gpus: 1, hw: 'AMD MI300X' },
  'mistralai/Mistral-Small-3.2-24B-Instruct-2506': { tps: 90, gpus: 1, hw: 'AMD MI300X' },
  'Qwen/Qwen3.8-27B-FP8': { tps: 85, gpus: 1, hw: 'AMD MI300X' },
  'google/gemma-4-31B-it': { tps: 75, gpus: 1, hw: 'AMD MI300X' },
  'zai-org/GLM-4.7': { tps: 60, gpus: 2, hw: 'AMD MI300X' },
  'zai-org/GLM-5.2': { tps: 55, gpus: 4, hw: 'AMD MI355X', monthlyEur: 10_000 },
  'zai-org/GLM-5.3': { tps: 55, gpus: 4, hw: 'AMD MI355X', monthlyEur: 10_000 },
  'moonshotai/Kimi-K2.6': { tps: 50, gpus: 8, hw: 'NVIDIA B300', monthlyEur: 45_000 },
  'moonshotai/Kimi-K3': { tps: 50, gpus: 8, hw: 'NVIDIA B300', monthlyEur: 45_000 },
  'meta-llama/Llama-3.3-70B-Instruct': { tps: 45, gpus: 2, hw: 'AMD MI300X' },
  'openai/gpt-oss-120b': { tps: 40, gpus: 4, hw: 'AMD MI300X' },
  'mistralai/Mistral-Medium-3.5-128B': { tps: 35, gpus: 4, hw: 'AMD MI300X' },
};

export const perfProfileFor = (modelId: string): ModelPerfProfile | undefined =>
  PROFILES[modelId] ?? PROFILES[modelId.replace(/-FP8$/, '')];

/** Single-stream output speed in tokens per second, if known. */
export const tpsFor = (modelId: string): number | undefined => perfProfileFor(modelId)?.tps;

/**
 * Grams of CO₂e for generating `tokens` tokens with the given model.
 * Returns undefined when the model has no profile, so callers can hide
 * the figure rather than present a fabricated one.
 */
function co2ForTokens(
  modelId: string,
  tokens: number,
  carbonIntensity: number = SWEDISH_CARBON_INTENSITY,
): number | undefined {
  const profile = perfProfileFor(modelId);
  if (!profile || tokens <= 0) return undefined;

  const joulesPerToken = (profile.gpus * GPU_WATTS) / (profile.tps * BATCH_FACTOR);
  const energyJoules = joulesPerToken * tokens;
  const energyKwh = energyJoules / JOULES_PER_KWH;

  const operational = energyKwh * carbonIntensity;

  /* GPU-hours consumed, then the share of embodied carbon they carry. */
  const gpuHours = (energyJoules / (profile.gpus * GPU_WATTS) / SECONDS_PER_HOUR) * profile.gpus;
  const embodied = (EMBODIED_CARBON_PER_GPU_KG / GPU_LIFETIME_HOURS) * gpuHours * 1_000;

  return operational + embodied;
}

/** Grams of CO₂e per million generated tokens — the figure shown on model cards. */
export const co2PerMillionTokens = (modelId: string): number | undefined =>
  co2ForTokens(modelId, 1_000_000);
